import React, { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Row, Col, Table, Card, CardBody } from 'reactstrap'
import { API_URL } from './utils/Constants/URLS'
import Modal from 'react-bootstrap/Modal'
import Button from 'react-bootstrap/Button'
import Select from 'react-select'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faDownload } from '@fortawesome/free-solid-svg-icons'

const SoDetails = () => {
  const token = localStorage.getItem('token')
  const [soData, setSoData] = useState({})
  const [managerDetails, setManagerDetails] = useState([])
  const [manager, setManager] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [showDeclineModal, setShowDeclineModal] = useState(false)
  const [reasonForRejection, setReasonForRejection] = useState('')
  const { soid } = useParams()
  const userDetails = useSelector(store => store.user)
  const navigate = useNavigate()

  // Fetch Sales Order Details
  useEffect(() => {
    const fetchSO = async () => {
      try {
        const res = await fetch(`${API_URL}leads/sales-orders/${soid}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        })
        const result = await res.json()
        setSoData(result?.data || {})
      } catch (e) {
        console.log(e)
      }
    }
    fetchSO()
  }, [token, soid])

  // Fetch Commercial Managers List
  useEffect(() => {
    const fetchCommMan = async () => {
      const url = `${API_URL}users/org-contacts?pagination=false&search=commercial_manager`
      try {
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        })
        const result = await response.json()
        setManagerDetails(
          result.data.docs.map(data => ({
            value: data._id,
            label: data.name
          }))
        )
      } catch (error) {
        console.error('Error fetching org contacts:', error)
      }
    }

    fetchCommMan()
  }, [token])

  const downloadFile = async filePath => {
    try {
      const url = `${API_URL}file/serve`

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ filePath })
      })

      if (!response.ok) return

      const blob = await response.blob()
      const downloadUrl = window.URL.createObjectURL(blob)

      const a = document.createElement('a')
      a.href = downloadUrl
      a.download = filePath.split('/').pop() || 'download'
      document.body.appendChild(a)
      a.click()
      a.remove()

      window.URL.revokeObjectURL(downloadUrl)
    } catch { }
  }

  const soActions = async (isApproval = true, rejectionReason = '') => {
    let statusText = ''

    if (isApproval) {
      if (userDetails?.role === 'commercial_manager') {
        statusText = 'approved by commercial manager'
      } else if (userDetails?.role === 'manager') {
        statusText = 'approved by manager'
      }
    } else {
      if (userDetails?.role === 'commercial_manager') {
        statusText = 'rejected'
      } else if (userDetails?.role === 'manager') {
        statusText = 'rejected'
      }
    }

    const requestBody = { status: statusText }
    if (!isApproval && rejectionReason) {
      requestBody.reasonForRejection = rejectionReason
    }

    try {
      const res = await fetch(`${API_URL}leads/sales-orders/${soid}/approve`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      })
      const result = await res.json()
      if (result.error === false) {
        alert(
          isApproval
            ? 'Sales Order approved successfully!'
            : 'Sales Order declined successfully!'
        )
        navigate('/view_leads')
      } else {
        alert(
          isApproval
            ? 'Error approving Sales Order'
            : 'Error declining Sales Order'
        )
      }
    } catch (e) {
      console.log(e)
    }
  }

  // Forward to Commercial Manager API
  const forwardToCommManager = async () => {
    try {
      const res = await fetch(
        `${API_URL}leads/sales-orders/${soid}/approval/request`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ commercialManager: manager })
        }
      )
      const result = await res.json()
      if (result.error) {
        throw new Error(result.message || 'Error forwarding to manager')
      }
    } catch (e) {
      console.log(e)
      alert('Error forwarding to commercial manager')
    }
  }

  // Handle Approve Button Click
  const handleApproveClick = () => {
    if (userDetails.role === 'manager') {
      // Manager needs to forward, so open modal
      setShowModal(true)
    } else if (userDetails.role === 'commercial_manager') {
      // Comm Manager directly approves
      soActions(true)
    }
  }

  // Handle Decline Button Click
  const handleDeclineClick = () => {
    setShowDeclineModal(true)
  }

  // Handle Decline Modal Close
  const handleCloseDeclineModal = () => {
    setShowDeclineModal(false)
    setReasonForRejection('')
  }

  // Handle Decline Confirmation
  const handleDeclineConfirm = async () => {
    if (!reasonForRejection.trim()) {
      alert('Please enter a reason for rejection')
      return
    }

    await soActions(false, reasonForRejection)
    setShowDeclineModal(false)
    setReasonForRejection('')
  }

  // Handle Proceed in Modal
  const handleProceed = async () => {
    if (!manager) {
      alert('Please select a Commercial Manager')
      return
    }

    await forwardToCommManager()
    await soActions(true)
    setShowModal(false)
  }

  const formatDate = date =>
    date
      ? new Date(date).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      })
      : '-'

  return (
    <Card className='p-4'>
      <CardBody>
        {/* Basic Details */}
        <Row>
          <Col md={4}>
            <label className='form-label'>
              <strong>Sales Order Number</strong>
            </label>
            <p>{soData?.salesOrderNumber || '-'}</p>
          </Col>
          <Col md={4}>
            <label className='form-label'>
              <strong>Business Line</strong>
            </label>
            <p>{soData?.businessLine?.name || '-'}</p>
          </Col>
          <Col md={4}>
            <label className='form-label'>
              <strong>Lead No</strong>
            </label>
            <p>{soData?.lead?.leadId || '-'}</p>
          </Col>
          <Col md={4}>
            <label className='form-label'>
              <strong>Sales Executive</strong>
            </label>
            <p>{soData?.lead?.leadOwner?.name || '-'}</p>
          </Col>
          <Col md={4}>
            <label className='form-label'>
              <strong>Customer Name</strong>
            </label>
            <p>{soData?.lead?.client?.companyName || '-'}</p>
          </Col>
          <Col md={4}>
            <label className='form-label'>
              <strong>Date of SO</strong>
            </label>
            <p>{formatDate(soData?.createdAt)}</p>
          </Col>
          <Col md={4}>
            <label className='form-label'>
              <strong>Zero Date Delivery</strong>
            </label>
            <p>{formatDate(soData?.zeroDateOfDelivery)}</p>
          </Col>
          {soData?.isPOAvailable === true ? (
            <>
              <Col md={4}>
                <label className='form-label'>
                  <strong>Customer PO Number</strong>
                </label>
                <p>
                  <Link
                    onClick={() => downloadFile(soData?.POFilePath)}
                    className='text-dark no-underline'
                  >
                    {soData?.customerPONumber || '-'}
                  </Link>
                </p>
              </Col>

              <Col md={4}>
                <label className='form-label'>
                  <strong>Customer PO Date</strong>
                </label>
                <p>{formatDate(soData?.customerPODate)}</p>
              </Col>
            </>
          ) : (
            <Col md={8}>
              <label className='form-label'>
                <strong>Po Details</strong>
              </label>
              <p>{soData?.PODetails}</p>
            </Col>
          )}

          <Col md={4}>
            <label className='form-label'>
              <strong>GSPL GSTIN</strong>
            </label>
            <p>{soData?.gsplGSTNumber || '-'}</p>
          </Col>
          <Col md={4}>
            <label className='form-label'>
              <strong>Consultant (If Any)</strong>
            </label>
            <p>{soData?.consultant || '-'}</p>
          </Col>
          <Col md={4}>
            <label className='form-label'>
              <strong>Currency of Transaction</strong>
            </label>
            <p>{soData?.currencyOfTransaction || '-'}</p>
          </Col>
          <Col md={4}>
            <label className='form-label'>
              <strong>Warranty Period from date of commissioning</strong>
            </label>
            <p>{soData?.warrantyPeriod || '-'} Months</p>
          </Col>
          <Col md={4}>
            <label className='form-label'>
              <strong>Warranty Period from date of last supply</strong>
            </label>
            <p>{soData?.warrantyTerms || '-'} Months</p>
          </Col>
          <Col md={4}>
            <label className='form-label'>
              <strong>Billing Address</strong>
            </label>
            <p>{soData?.billingAddress || '-'}</p>
          </Col>
          <Col md={4}>
            <label className='form-label'>
              <strong>Shipping Address</strong>
            </label>
            <p>{soData?.shippingAddress || '-'}</p>
          </Col>
          <Col md={4}>
            <label className='form-label'>
              <strong>Remarks</strong>
            </label>
            <p>{soData?.remarks || '-'}</p>
          </Col>
          <Col md={4}>
            <label className='form-label'>
              <strong>BOM</strong>
            </label>
            <p>
              <button
                type='button'
                className='btn download-btn mr-2 '
                onClick={() => downloadFile(soData?.BOMFilePath)}
              >
                <FontAwesomeIcon icon={faDownload} />
              </button>
            </p>
          </Col>
        </Row>

        {/* Line Items Table */}
        <div className='avoid-page-break'>
          <Table className='so-table' responsive>
            <thead>
              <tr>
                <th>SI</th>
                <th>Name</th>
                <th>Part Code</th>
                <th>UOM</th>
                <th>Qty</th>
                <th>Std. Lead Time</th>
                <th>Date</th>
                <th>LP</th>
                <th>Discount</th>
                <th>Rate Per Unit</th>
                <th>Order Value</th>
              </tr>
            </thead>
            <tbody>
              {soData?.lineItems?.map((data, index) => (
                <tr key={index}>
                  <td>{index + 1}</td>
                  <td>{data?.model?.modelName}</td>
                  <td>{data?.model?.partCode}</td>
                  <td>{data?.model?.uom}</td>
                  <td>{data?.quantity}</td>
                  <td>{data?.standardLeadTime}</td>
                  <td>{formatDate(data?.committedDeliveryDate)}</td>
                  <td>{(data?.listPrice || 0).toFixed(2)}</td>
                  <td>{(data?.discount || 0).toFixed(2)}%</td>
                  <td>{(data?.rate || 0).toFixed(2)}</td>
                  <td>{(data?.value || 0).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>

        {/* Payment Terms Table */}
        <Row className='mt-3'>
          <Col md={8}>
            <div className='avoid-page-break'>
              <Table className='so-table'>
                <thead>
                  <tr>
                    <th>Payment Terms</th>
                    <th>Percentage</th>
                    <th>Amount</th>
                    <th>Other Details</th>
                  </tr>
                </thead>
                <tbody>
                  {soData?.paymentTerms?.map((data, index) => {
                    const totalValue = soData?.lineItems?.reduce(
                      (acc, item) => acc + (item.value || 0),
                      0
                    )
                    const computedPercentage = totalValue
                      ? ((data?.amount / totalValue) * 100).toFixed(2)
                      : 0

                    return (
                      <tr key={index}>
                        <td>{data?.type || '-'}</td>
                        <td>{computedPercentage}%</td>
                        <td>{(data?.amount || 0).toFixed(2)}</td>
                        <td>{data?.otherDetails || ''}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </Table>

              {/* Extra Details Table */}
              <Table className='so-table'>
                <tbody>
                  <tr>
                    <td>
                      <strong>Price Basis:</strong>
                    </td>
                    <td>{soData?.priceBasis || '-'}</td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Responsibility of transportation:</strong>
                    </td>
                    <td>{soData?.responsibilityOfTransportation || '-'}</td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Type of Transaction:</strong>
                    </td>
                    <td>{soData?.typeOfTransaction || '-'}</td>
                  </tr>
                  <tr>
                    <td>
                      <strong>GST Details:</strong>
                    </td>
                    <td>GST extra as applicable</td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Bank Guarantee Details:</strong>
                    </td>
                    <td>{soData?.bankGuaranteeDetails || '-'}</td>
                  </tr>
                </tbody>
              </Table>
            </div>
          </Col>
        </Row>

        {/* Approve / Decline Buttons */}
        <Row className='mt-4'>
          <Col className='d-flex justify-content-end gap-2'>
            <button
              type='button'
              onClick={handleApproveClick}
              className='btn btn-success'
            >
              Approve
            </button>
            <button
              type='button'
              onClick={handleDeclineClick}
              className='btn btn-danger'
            >
              Decline
            </button>
          </Col>
        </Row>
      </CardBody>

      {/* Modal for Manager Role */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header className='bg-blue-100'>
          <Modal.Title>Forward to Commercial Manager</Modal.Title>
        </Modal.Header>
        <form className='row g-3 needs-validation' noValidate>
          <Modal.Body>
            <div>
              <label
                htmlFor='managerSelect'
                className='block text-gray-700 font-medium mb-2'
              >
                Please select a Commercial Manager to whom the further approval
                process will move on
              </label>

              <Select
                id='managerSelect'
                className='text-sm'
                classNamePrefix='react-select'
                options={managerDetails}
                placeholder='Select Manager'
                value={
                  managerDetails.find(option => option.value === manager) ||
                  null
                }
                onChange={option => setManager(option ? option.value : '')}
                isClearable
              />
            </div>
          </Modal.Body>

          <Modal.Footer>
            <Button
              type='button'
              onClick={handleProceed}
              className='btn add-btn'
            >
              Proceed
            </Button>
          </Modal.Footer>
        </form>
      </Modal>

      {/* Decline Modal */}
      <Modal show={showDeclineModal} onHide={handleCloseDeclineModal} centered>
        <Modal.Header className='bg-red-100' closeButton>
          <Modal.Title>Decline Sales Order</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form>
            <div className='my-8 text-center'>
              <textarea
                className='form-control h-full'
                value={reasonForRejection}
                onChange={e => setReasonForRejection(e.target.value)}
                placeholder='Enter The Reason for Declining The Sales Order'
                rows={4}
              />
            </div>

            <div className='flex max-sm:flex-col gap-4'>
              <button
                onClick={handleCloseDeclineModal}
                type='button'
                className='px-5 py-2.5 rounded-lg w-full tracking-wide text-gray-800 text-sm border-none outline-none bg-gray-200 hover:bg-gray-300'
              >
                Cancel
              </button>
              <button
                type='button'
                onClick={handleDeclineConfirm}
                className='px-5 py-2.5 rounded-lg w-full tracking-wide text-white text-sm border-none outline-none bg-red-500 hover:bg-red-600'
              >
                Decline
              </button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </Card>
  )
}

export default SoDetails
